import type { CSSProperties, Dispatch, RefObject, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Placement as FloatPlacement } from "@floating-ui/react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import type { SealedInitialState } from "reakit-utils/useSealedState";
import { useSealedState } from "reakit-utils/useSealedState";
import type { DialogActions, DialogInitialState, DialogState, DialogStateReturn } from "reakit";
import { useDialogState } from "reakit";

type Placement = FloatPlacement | "auto-start" | "auto-end" | "auto";

export type PopoverState = DialogState & {
  /**
   * The reference element.
   */
  unstable_referenceRef: RefObject<HTMLElement | null>;
  /**
   * The popover element.
   * @private
   */
  unstable_popoverRef: RefObject<HTMLElement | null>;
  /**
   * The arrow element.
   * @private
   */
  unstable_arrowRef: RefObject<HTMLElement | null>;
  /**
   * Popover styles.
   * @private
   */
  unstable_popoverStyles: CSSProperties;
  /**
   * Arrow styles.
   * @private
   */
  unstable_arrowStyles: CSSProperties;
  /**
   * `placement` passed to the hook.
   * @private
   */
  unstable_originalPlacement: Placement;
  /**
   * @private
   */
  unstable_update: () => boolean;
  /**
   * Actual `placement`.
   */
  placement: Placement;
};

export type PopoverActions = DialogActions & {
  /**
   * Change the `placement` state.
   */
  place: Dispatch<SetStateAction<Placement>>;
};

export type PopoverInitialState = DialogInitialState &
  Partial<Pick<PopoverState, "placement">> & {
    /**
     * Whether or not the popover should have `position` set to `fixed`.
     */
    fixed?: boolean;
    /**
     * Offset between the reference and the popover: [main axis, alt axis]. Should not be combined with `gutter`.
     */
    offset?: number | null;
    /**
     * Offset between the reference and the popover on the main axis. Should not be combined with `unstable_offset`.
     */
    gutter?: number;
    /**
     * Prevents popover from being positioned outside the boundary.
     */
    unstable_preventOverflow?: boolean;
  };

export type PopoverStateReturn = DialogStateReturn & PopoverState & PopoverActions;

function getAllowedPlacement(placement: Placement): FloatPlacement {
  if (placement === "auto") {
    return "bottom";
  }
  if (placement === "auto-start") {
    return "top-start";
  }

  if (placement === "auto-end") {
    return "bottom-end";
  }

  return placement;
}

export function usePopoverState(
  initialState: SealedInitialState<PopoverInitialState> = {}
): PopoverStateReturn {
  const {
    gutter = 12,
    placement: sealedPlacement = "bottom",
    offset: sealedOffset,
    unstable_preventOverflow: preventOverflow = true,
    fixed = true,
    modal = false,
    ...sealed
  } = useSealedState(initialState);

  const [originalPlacement, setPlacement] = useState<FloatPlacement>(
    getAllowedPlacement(sealedPlacement)
  );

  useEffect(() => {
    setPlacement(getAllowedPlacement(sealedPlacement));
  }, [sealedPlacement]);

  const arrowRef = useRef<HTMLElement>(null);
  const dialog = useDialogState({ modal, ...sealed });

  const float = useFloating({
    placement: originalPlacement,
    open: dialog.visible,
    strategy: fixed ? "fixed" : "absolute",
    whileElementsMounted: autoUpdate,
    middleware: [
      shift({ padding: 4 }),
      offset(sealedOffset ?? gutter),
      flip({
        fallbackAxisSideDirection: "end",
        fallbackStrategy: "initialPlacement",
      }),
    ],
  });

  const update = useCallback(() => {
    float.update();
    return true;
  }, [float]);

  return {
    ...dialog,
    unstable_referenceRef: float.refs.setReference as unknown as RefObject<HTMLElement | null>,
    unstable_popoverRef: float.refs.setFloating as unknown as RefObject<HTMLElement | null>,
    unstable_arrowRef: arrowRef,
    unstable_popoverStyles: float.floatingStyles,
    unstable_arrowStyles: {},
    unstable_update: update,
    unstable_originalPlacement: originalPlacement,
    placement: float.placement,

    /* Т.к. мы переписали usePopoverState с использованием floating-ui, типы placement в нашем usePopoverState и в Popover из reakit перестали совпадать. Эта конструкция приводит их к единообразию. */
    place(placement: Placement | ((value: Placement) => Placement)) {
      /* тип аргументов соответствует useState */
      if (isPlacement(placement)) {
        return setPlacement(getAllowedPlacement(placement));
      } else {
        setPlacement((currentPlacement) => getAllowedPlacement(placement(currentPlacement)));
      }
    },
  };
}

// type guard
function isPlacement(arg: unknown): arg is Placement {
  return typeof arg === "string";
}
